import React, { useContext, useEffect, useState } from 'react'
import { navigate } from 'gatsby'
import { useFormik } from 'formik'
import classNames from 'classnames'
import { captureException } from '@sentry/gatsby'
import heapAnalytics from '@toasttab/shared/utils/heapAnalytics'
import { KeyValuePair } from '@toasttab/shared/src/interfaces/Interface.d'
import {
  Button,
  CheckboxInput,
  SelectInput
} from '@toasttab/shared/src/components'
import FormField from '@toasttab/shared/src/components/atoms/Forms/FormField/FormField'
import TextInput from '@toasttab/shared/src/components/atoms/Forms/TextInput/TextInput'
import { fillOptions, postForm } from '@toasttab/shared/src/utils/formUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import { getInitialFormValues } from '@toasttab/shared/src/hooks/useDataCapture'
import { SubscriptionFormProps } from './SubscriptionForm.d'
import HiddenFields from '../HiddenFields'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { OTLContext } from '@toasttab/shared/src/context/OTLContext'

const SubscriptionForm = ({
  addToSidebar = false,
  buttonText = 'Subscribe',
  classes,
  craftFieldsetSlug = 'zip-only', // to bypass user location on Craft CMS side
  darkMode = false,
  easyAccess = false,
  formCategory,
  marketoFormID = '1008',
  marketoProgramName = 'Subscribe-to-On-the-Line',
  onAfterSubmitting,
  returnUrl = '/subscribe-thank-you',
  showPrivacyStatement
}: SubscriptionFormProps) => {
  const [countryCode, setCountryCode] = useState(null)
  const [isFormSubmitError, setFormSubmitError] = useState(false)
  const [isSubmitted, setSubmitted] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [postalCode, setPostalCode] = useState(null)
  const otlParams = useContext(OTLContext)

  const { geoData, loading } = useGeoLocation()
  const { prefix, language, t } = useI18next()

  const OTLArticleUX: boolean = Boolean(otlParams.isArticle)
  const conversionType =
    formCategory && formCategory.title === 'Email Only' ? 'OT' : 'MEL-HEADLESS'

  const formWrapperCss = classNames(classes, {
    'mb-2 xl:mb-0 flex flex-col': true,
    'xl:flex-row': !OTLArticleUX,
    'md:flex-row': OTLArticleUX
  })

  const errorClasses = classNames('text-otl-error text-xs text-left', {
    'mb-2 md:mb-1 md:mt-[-24px]': showPrivacyStatement
  })

  const disclaimerClasses = classNames(
    'text-xs font-calibre text-legacy-grey-10 text-center',
    {
      'mt-4 md:mt-[-12px]': !isFormSubmitError,
      'pb-2': isFormSubmitError
    }
  )

  //const { pathPrefix } = useSiteMetadata()

  useEffect(() => {
    if (geoData) {
      setPostalCode(geoData?.postal?.code)
      setCountryCode(geoData?.country?.iso_code)
    }
  }, [loading])

  const initValues: KeyValuePair = {
    craftFieldsetSlug: `${craftFieldsetSlug}`, // using craftFieldsetSlug zip-only so Craft woudn't do the look up to override the current user postal code
    craftRestaurantToast: '', // honeypot, purposefully empty, if this is filled then assuming it's bot
    munchkinId: `${process.env.GATSBY_FORM_MUNCHKINID ?? '713-DII-842'}`,
    formid: `${marketoFormID}`,
    eI: '1',
    browserLanguage: 'en',
    referrerURL: `${process.env.GATSBY_CMS_URL}${
      typeof window !== 'undefined' && window.location.pathname
    }`,
    _mktoReferrer: `${typeof window !== 'undefined' && window.location.href}`,
    _mkt_trk: `${_helpers.getCookie('_mkto_trk')}`,
    mkoProgramName: `${marketoProgramName}${
      language == 'en' ? '' : '-' + language.toUpperCase()
    }`,
    returnURL: `${prefix}${returnUrl}`,
    email: _helpers.getCookie('email'),
    referringPageURLText: ``,
    craftJavascriptStatus: `Loaded`,
    shownCalendly: false,
    recaptchaValid: `no`,
    appendVariantTest: ``,
    craftFormLocation: `NOT SET`,
    gae: ``
  }

  const { initialValues } = getInitialFormValues(initValues)

  const validateFormSubmission = (values: KeyValuePair) => {
    setFormSubmitError(false)
    const errors: KeyValuePair = {}

    formCategory &&
      formCategory.coreFields &&
      formCategory.coreFields.map((f: any) => {
        if (f.fieldRequired && !values[f.fieldInputNameMarketoRestApi]) {
          errors[
            f.fieldInputNameMarketoRestApi
          ] = `${f.fieldInputLabel} is required`
        } else if (f.fieldInputType.toLowerCase() == 'email') {
          if (_helpers.validateEmail(values[f.fieldInputNameMarketoRestApi])) {
            errors.email = 'Invalid email address'
          } else {
            // @ts-ignore - Typescript doesn't like setting new properties on window
            let heapHandler = window.heap
            if (
              typeof window !== 'undefined' &&
              typeof heapHandler !== 'undefined'
            ) {
              heapHandler.addUserProperties({
                email: values[f.fieldInputNameMarketoRestApi].toLowerCase()
              })
              heapHandler.identify(
                values[f.fieldInputNameMarketoRestApi].toLowerCase()
              )
            }
          }
        } else if (f.fieldInputType.toLowerCase() == 'tel') {
          // currently otl doesnt validate phone
        }
      })

    return errors
  }

  // Pass the useFormik() hook initial form values and a submit function that will
  // be called when the form is submitted
  const formik = useFormik({
    initialValues: initialValues,
    validate: validateFormSubmission,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values: any) => {
      const formData = new FormData()
      formData.append('craftInferredCountry', countryCode)
      formData.append('postalCode', postalCode)
      for (const value in values) {
        if (value === 'email') {
          _helpers.createCookie('email', values[value], 525600, '/')
        }

        formData.append(value, values[value])
      }
      setSubmitting(true)

      postForm(formData)
        .then((response) => {
          setSubmitting(false)
          if (response.redirected) {
            setSubmitted(true)
            onAfterSubmitting && onAfterSubmitting()
            navigate(response.url)
          }
        })
        .catch(function (err) {
          captureException(`${err} - ${JSON.stringify(values, null, 2)}`)
          setSubmitting(false)
          setFormSubmitError(true)
        })
    }
  })

  const renderStandardFields = () => {
    return (
      <>
        {formCategory &&
          formCategory.coreFields &&
          formCategory.coreFields.map((f: any, i: number) => {
            if (
              f.fieldInputType.toLowerCase() ===
              'checkbox (single/multi select)'
            )
              return null
            return (
              <FormField key={i} hidden={easyAccess} fullWidth>
                {f.fieldInputType.toLowerCase() === 'text' && (
                  <TextInput
                    type='text'
                    name={f.fieldInputNameMarketoRestApi}
                    id={f.fieldInputName}
                    placeholder={f.fieldInputLabel}
                    fullWidth
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                    error={
                      formik.errors[f.fieldInputNameMarketoRestApi]
                        ? (formik.errors[
                            f.fieldInputNameMarketoRestApi
                          ] as string[])
                        : ''
                    }
                  />
                )}

                {f.fieldInputType.toLowerCase() === 'email' && (
                  <TextInput
                    type='email'
                    name={f.fieldInputNameMarketoRestApi}
                    id={f.fieldInputName}
                    placeholder={f.fieldInputLabel}
                    fullWidth
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                    variation={!OTLArticleUX ? 'otl-default' : 'default'}
                    padding='medium'
                    required={f.isRequired}
                    error={
                      formik.errors[f.fieldInputNameMarketoRestApi]
                        ? (formik.errors[
                            f.fieldInputNameMarketoRestApi
                          ] as string[])
                        : ''
                    }
                  />
                )}
                {f.fieldInputType.toLowerCase() === 'phone' && (
                  <TextInput
                    type='tel'
                    name={f.fieldInputNameMarketoRestApi}
                    id={f.fieldInputName}
                    placeholder={f.fieldInputLabel}
                    fullWidth
                    onChange={formik.handleChange}
                    value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                    error={
                      formik.errors[f.fieldInputNameMarketoRestApi]
                        ? (formik.errors[
                            f.fieldInputNameMarketoRestApi
                          ] as string[])
                        : ''
                    }
                  />
                )}
                {f.fieldInputType.toLowerCase() == 'select (dropdown)' && (
                  <SelectInput
                    id={f.fieldInputName}
                    name={f.fieldInputNameMarketoRestApi}
                    label={f.fieldInputLabel}
                    options={fillOptions(
                      f.fieldInputLabel,
                      f.fieldInputOptions
                    )}
                    fullWidth
                    onChange={formik.handleChange}
                    error={
                      formik.errors[f.fieldInputNameMarketoRestApi]
                        ? (formik.errors[
                            f.fieldInputNameMarketoRestApi
                          ] as string[])
                        : ''
                    }
                  />
                )}
              </FormField>
            )
          })}

        <HiddenFields
          formik={formik}
          countryCode={countryCode}
          postalCode={postalCode}
        />
      </>
    )
  }

  const renderCheckboxField = () => {
    return (
      <>
        {formCategory &&
          formCategory.coreFields &&
          formCategory.coreFields.map((f: any, i: number) => {
            if (
              f.fieldInputType.toLowerCase() !==
              'checkbox (single/multi select)'
            )
              return null
            return (
              <div key={i} className='mb-5'>
                {f.fieldInputType.toLowerCase() ===
                  'checkbox (single/multi select)' && (
                  <CheckboxInput
                    id={f.fieldInputName}
                    name={f.fieldInputNameMarketoRestApi}
                    labels={null}
                    label={
                      f.fieldOptions &&
                      f.fieldOptions[0].key + (f.isRequired ? '*' : '')
                    }
                    onChange={formik.handleChange}
                    value={formik.values[f.fieldInputNameMarketoRestApi]}
                    variation='default'
                    darkMode={darkMode}
                  />
                )}
              </div>
            )
          })}
      </>
    )
  }

  return (
    <>
      <form
        id={`mktForm_${marketoFormID}`}
        data-heap-mktform-id={marketoFormID}
        aria-label='Subscription Form'
        onSubmit={(e) => {
          e.preventDefault()
          if (formik.isValid) {
            formik.submitForm()
          }
        }}
        data-ct={conversionType}
        data-testid={isSubmitted ? 'form-submitted' : 'form-not-submitted'}
        className={'md:mt-[-16px]'}
        noValidate
      >
        <div className={formWrapperCss}>
          {renderStandardFields()}
          <div
            className={`${
              addToSidebar ? 'xl:pl-2 w-1/2' : 'pl-0 w-full xl:pl-4 md:w-1/4'
            }`}
          >
            <Button
              type='submit'
              disabled={isSubmitting}
              arrowHover={!isSubmitting && !OTLArticleUX}
              fullwidth
              classes={OTLArticleUX && 'md:ml-2 lg:ml-0'}
              variation={!OTLArticleUX ? 'primary-otl' : 'primary'}
            >
              {isSubmitting && (
                <div className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] '>
                  <img
                    src='https://toast-craft-assets.s3.amazonaws.com/spinner.gif'
                    className='w-[43px] h-[43px] grayscale'
                    alt='loadingspinner'
                  />
                </div>
              )}
              <div
                className={
                  isSubmitting ? 'invisible' : 'font-semibold uppercase'
                }
              >
                {buttonText}
              </div>
            </Button>
          </div>
        </div>
        {renderCheckboxField()}
      </form>

      {isFormSubmitError && (
        <div className={errorClasses}>
          {t(`There was an error trying to submit the form.`)}
        </div>
      )}
      {showPrivacyStatement && (
        <p className={disclaimerClasses}>
          {t(
            `By submitting, you agree to receive marketing emails from Toast. We’ll handle your info according to our`
          )}{' '}
          <a href='/privacy' className='text-toast-orange-500'>
            {t(`privacy statement`)}
          </a>
          {t(`. Additional information for California residents available`)}{' '}
          <a href='/privacy#addendum-a' className='text-toast-orange-500'>
            {t(`here`)}
          </a>
        </p>
      )}
    </>
  )
}

export default SubscriptionForm
